import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addObserveAsync } from "./AddObserveAsync";
import { Observe } from "../../../api/controlPlaneApi";
import { IFieldStore } from "../types";
import {
  MinioEnvVar,
  MinioImagePullPolicy,
  MinioTaintEffect,
  MinioToleration,
  MinioTolerationOperator,
} from "api/observeRpcApi";
import { LabelKeyPair } from "screens/common/types";

export interface IAddObserve {
  accessKey: string;
  addObserveLoading: boolean;
  envVars: MinioEnvVar[];
  image?: string;
  imagePullPolicy: MinioImagePullPolicy;
  imagePullSecrets: string[];
  imageRegistry: string;
  imageRegistryPassword: string;
  imageRegistryUsername: string;
  ingest?: IObserveIngest;
  isCustomRegistry: boolean;
  isCustomRegistryCreds: boolean;
  minioURL: string;
  name: string;
  nameError: string;
  namespace: string;
  hideNamespace: boolean;
  nodeSelectorPairs: LabelKeyPair[];
  observeProcessed: Observe | null;
  resourcesCPULimit?: string;
  resourcesCPULimitError: string;
  resourcesCPURequest?: string;
  resourcesCPURequestError: string;
  resourcesMemoryLimit?: string;
  resourcesMemoryLimitError: string;
  resourcesMemoryRequest?: string;
  resourcesMemoryRequestError: string;
  resourcesSpecifyLimit: boolean;
  secretKey: string;
  selectedSite: string;
  storageClass: string;
  storageClassDB: string;
  tolerations: MinioToleration[];
  validPages: string[];
  volumeSize: string;
  volumeSizeDB: string;
  volumeSizeFactor: string;
  volumeSizeFactorDB: string;
  dbEnabled: boolean;
  tenantReference: string | null;
}

export interface IObserveIngest {
  backupInterval?: string;
  enableBackup?: boolean;
  minioBucket?: string;
  minioBucketPrefix?: string;
  minioHostURL?: string;
  minioRegion?: string;
  peers?: string;
  replicas?: number;
  url?: string;
}

const initialState: IAddObserve = {
  accessKey: "",
  addObserveLoading: false,
  dbEnabled: true,
  envVars: [{ name: "", value: "" }],
  imagePullPolicy: MinioImagePullPolicy.IfNotPresent,
  imagePullSecrets: [""],
  imageRegistry: "",
  imageRegistryPassword: "",
  imageRegistryUsername: "",
  isCustomRegistry: false,
  isCustomRegistryCreds: false,
  minioURL: "",
  name: "",
  nameError: "",
  namespace: "",
  hideNamespace: false,
  observeProcessed: null,
  resourcesCPULimitError: "",
  resourcesCPURequestError: "",
  resourcesMemoryLimitError: "",
  resourcesMemoryRequestError: "",
  resourcesSpecifyLimit: false,
  secretKey: "",
  selectedSite: "",
  storageClass: "",
  storageClassDB: "",
  // We can assume all the other pages are valid with default configuration
  validPages: ["setup", "configure", "images"],
  volumeSize: "100",
  volumeSizeDB: "100",
  volumeSizeFactor: "Gi",
  volumeSizeFactorDB: "Gi",
  nodeSelectorPairs: [{ key: "", value: "" }],
  tolerations: [
    {
      key: "",
      seconds: `${0}`,
      value: "",
      effect: MinioTaintEffect.NoSchedule,
      operator: MinioTolerationOperator.Equal,
    },
  ],
  tenantReference: null,
};

export const addObserveSlice = createSlice({
  name: "addObserve",
  initialState,
  reducers: {
    setAddObserveLoading: (state, action: PayloadAction<boolean>) => {
      state.addObserveLoading = action.payload;
    },
    setObserveSelectedSite: (state, action: PayloadAction<string>) => {
      state.selectedSite = action.payload;
    },
    setObserveName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setObserveNameError: (state, action: PayloadAction<string>) => {
      state.nameError = action.payload;
    },
    setObserveNamespace: (state, action: PayloadAction<string>) => {
      state.namespace = action.payload;
    },
    hideObserveNamespace: (state) => {
      state.hideNamespace = true;
    },
    setObserveVolumeSize: (state, action: PayloadAction<string>) => {
      state.volumeSize = action.payload;
    },
    setObserveStorageClass: (state, action: PayloadAction<string>) => {
      state.storageClass = action.payload;
    },
    setObserveMinIOURL: (state, action: PayloadAction<string>) => {
      state.minioURL = action.payload;
    },
    setObserveAccessKey: (state, action: PayloadAction<string>) => {
      state.accessKey = action.payload;
    },
    setObserveSecretKey: (state, action: PayloadAction<string>) => {
      state.secretKey = action.payload;
    },
    setObserveVolumeSizeFactor: (state, action: PayloadAction<string>) => {
      state.volumeSizeFactor = action.payload;
    },
    setObserveVolumeSizeFactorDB: (state, action: PayloadAction<string>) => {
      state.volumeSizeFactorDB = action.payload;
    },
    setObserveCPULimit: (state, action: PayloadAction<string | undefined>) => {
      state.resourcesCPULimit = action.payload;
    },
    setObserveCPURequest: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.resourcesCPURequest = action.payload;
    },
    setObserveCPULimitError: (state, action: PayloadAction<string>) => {
      state.resourcesCPULimitError = action.payload;
    },
    setObserveCPURequestError: (state, action: PayloadAction<string>) => {
      state.resourcesCPURequestError = action.payload;
    },
    setObserveMemoryLimit: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.resourcesMemoryLimit = action.payload;
    },
    setObserveMemoryRequest: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.resourcesMemoryRequest = action.payload;
    },
    setObserveMemoryRequestError: (state, action: PayloadAction<string>) => {
      state.resourcesMemoryRequestError = action.payload;
    },
    setObserveMemoryLimitError: (state, action: PayloadAction<string>) => {
      state.resourcesMemoryLimitError = action.payload;
    },
    setObserveResourcesSpecifyLimit: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.resourcesSpecifyLimit = action.payload;
    },
    setObserveImage: (state, action: PayloadAction<string | undefined>) => {
      state.image = action.payload;
    },
    setObserveIsCustomRegistry: (state, action: PayloadAction<boolean>) => {
      state.isCustomRegistry = action.payload;
    },
    setObserveIsCustomRegistryCreds: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isCustomRegistryCreds = action.payload;
    },
    setObserveImageRegistry: (state, action: PayloadAction<string>) => {
      state.imageRegistry = action.payload;
    },
    setObserveImageRegistryPassword: (state, action: PayloadAction<string>) => {
      state.imageRegistryPassword = action.payload;
    },
    setObserveImageRegistryUsername: (state, action: PayloadAction<string>) => {
      state.imageRegistryUsername = action.payload;
    },
    setObserveImagePullPolicy: (
      state,
      action: PayloadAction<MinioImagePullPolicy>,
    ) => {
      state.imagePullPolicy = action.payload;
    },
    setObserveImagePullSecrets: (state, action: PayloadAction<string[]>) => {
      state.imagePullSecrets = action.payload;
    },

    setObserveEnvVars: (state, action: PayloadAction<MinioEnvVar[]>) => {
      state.envVars = action.payload;
    },
    setObserveIngestURL: (state, action: PayloadAction<string | undefined>) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.url = action.payload;
    },
    setObserveIngestReplicas: (
      state,
      action: PayloadAction<number | undefined>,
    ) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.replicas = action.payload;
    },
    setObserveIngestPeers: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.peers = action.payload;
    },
    setObserveIngestEnableBackup: (
      state,
      action: PayloadAction<boolean | undefined>,
    ) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.enableBackup = action.payload;
    },
    setObserveIngestBackupInterval: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.backupInterval = action.payload;
    },
    setObserveIngestMinioHostURL: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.minioHostURL = action.payload;
    },
    setObserveIngestMinioRegion: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.minioRegion = action.payload;
    },
    setObserveIngestMinioBucket: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.minioBucket = action.payload;
    },
    setObserveIngestMinioBucketPrefix: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      if (!state.ingest) {
        state.ingest = {};
      }
      state.ingest.minioBucketPrefix = action.payload;
    },
    setNodeSelectorPairs: (state, action: PayloadAction<LabelKeyPair[]>) => {
      state.nodeSelectorPairs = action.payload;
    },
    setObserveTolerations: (
      state,
      action: PayloadAction<{
        index: number;
        tolerationValue: MinioToleration;
      }>,
    ) => {
      state.tolerations[action.payload.index] = action.payload.tolerationValue;
    },
    addNewObserveToleration: (state) => {
      const newTolerationArray = [
        ...state.tolerations,
        {
          key: "",
          seconds: "0",
          value: "",
          effect: MinioTaintEffect.NoSchedule,
          operator: MinioTolerationOperator.Equal,
        },
      ];
      state.tolerations = newTolerationArray;
    },
    removeObserveToleration: (state, action: PayloadAction<number>) => {
      state.tolerations = state.tolerations.filter(
        (_, index) => index !== action.payload,
      );
    },
    resetAddObserveForm: () => initialState,
    storeObservePageValidity: (
      state,
      action: PayloadAction<{
        pageName: keyof IFieldStore;
        valid: boolean;
      }>,
    ) => {
      let originValidPages = state.validPages;
      if (action.payload.valid) {
        if (!originValidPages.includes(action.payload.pageName)) {
          originValidPages.push(action.payload.pageName);

          state.validPages = [...originValidPages];
        }
      } else {
        const newSetOfPages = originValidPages.filter(
          (elm) => elm !== action.payload.pageName,
        );
        state.validPages = [...newSetOfPages];
      }
    },

    setObserveVolumeSizeDB: (state, action: PayloadAction<string>) => {
      state.volumeSizeDB = action.payload;
    },
    setObserveStorageClassDB: (state, action: PayloadAction<string>) => {
      state.storageClassDB = action.payload;
    },
    setObserveDBEnabled: (state, action: PayloadAction<boolean>) => {
      state.dbEnabled = action.payload;
    },
    setTenantReference: (state, action: PayloadAction<string | null>) => {
      state.tenantReference = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addObserveAsync.pending, (state) => {
        state.addObserveLoading = true;
      })
      .addCase(addObserveAsync.rejected, (state) => {
        state.addObserveLoading = false;
      })
      .addCase(addObserveAsync.fulfilled, (state, action) => {
        state.addObserveLoading = false;
        state.observeProcessed = action.payload;
      });
  },
});

export const {
  addNewObserveToleration,
  hideObserveNamespace,
  removeObserveToleration,
  resetAddObserveForm,
  setAddObserveLoading,
  setNodeSelectorPairs,
  setObserveAccessKey,
  setObserveCPULimit,
  setObserveCPULimitError,
  setObserveCPURequest,
  setObserveCPURequestError,
  setObserveDBEnabled,
  setObserveEnvVars,
  setObserveImage,
  setObserveImagePullPolicy,
  setObserveImagePullSecrets,
  setObserveImageRegistry,
  setObserveImageRegistryPassword,
  setObserveImageRegistryUsername,
  setObserveIngestBackupInterval,
  setObserveIngestEnableBackup,
  setObserveIngestMinioBucket,
  setObserveIngestMinioBucketPrefix,
  setObserveIngestMinioHostURL,
  setObserveIngestMinioRegion,
  setObserveIngestPeers,
  setObserveIngestReplicas,
  setObserveIngestURL,
  setObserveIsCustomRegistry,
  setObserveIsCustomRegistryCreds,
  setObserveMemoryLimit,
  setObserveMemoryLimitError,
  setObserveMemoryRequest,
  setObserveMemoryRequestError,
  setObserveMinIOURL,
  setObserveName,
  setObserveNameError,
  setObserveNamespace,
  setObserveResourcesSpecifyLimit,
  setObserveSecretKey,
  setObserveSelectedSite,
  setObserveStorageClass,
  setObserveStorageClassDB,
  setObserveTolerations,
  setObserveVolumeSize,
  setObserveVolumeSizeDB,
  setObserveVolumeSizeFactor,
  setObserveVolumeSizeFactorDB,
  storeObservePageValidity,
  setTenantReference,
} = addObserveSlice.actions;

export default addObserveSlice.reducer;
